import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { AgGridReact } from 'ag-grid-react'
import { Box, Switch, Typography } from '@mui/material'
import { ChainProtocol, PayoutToken, Token, TokenHistoryOperation } from '@archax/shared-types'
import { ColDef, GridOptions, ICellRendererParams, IGetRowsParams } from 'ag-grid-community'
import RefreshIcon from '@mui/icons-material/Refresh'
import { getPayoutHistory } from '../../../api/payouts'
import { ExplorerLinks } from '../../../components/ui/ExplorerLinks'
import { DEFAULT_PAGE_SIZE } from '../../../constants'
import { formatDate } from '../../../util/formatters'
import tokenBalanceFormatter from '../../../util/token-balance-formatter'

type Props = {
  payoutContractId: string
  payoutToken?: PayoutToken
}
export function History({ payoutContractId, payoutToken }: Props) {
  const { t } = useTranslation()
  const gridRef = useRef<AgGridReact | null>(null)
  const [showFailed, setShowFailed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const paginationState = useRef({
    timestamp: undefined,
    totalRowCount: 0,
  })

  const columnDefs: ColDef<any>[] = useMemo(
    () => [
      {
        field: 'explorer',
        headerName: '',
        cellRenderer: (params: ICellRendererParams<any>) => {
          const explorerLinkParams = {
            explorerLinks: params.data?.explorerLinks,
            token: {
              standard: {
                chain: {
                  protocol: ChainProtocol.Hedera,
                },
              },
            } as Token,
          }
          return <ExplorerLinks operation={explorerLinkParams as TokenHistoryOperation} />
        },
        width: 70,
        maxWidth: 70,
        flex: 1,
        sortable: false,
        filter: false,
        cellStyle: { padding: '0', display: 'flex', justifyContent: 'center', alignItems: 'center' },
      },
      {
        field: 'status',
        cellRenderer: (params: ICellRendererParams<any>) => {
          if (params.data) {
            return params.data?.result === 'SUCCESS' ? 'Success' : 'Error'
          }
          return ''
        },
        headerName: t('payoutContracts.labels.status'),
        width: 150,
        maxWidth: 150,
        flex: 1,
        sortable: false,
        filter: false,
        floatingFilter: false,
        cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 0 },
      },
      {
        field: 'created_at',
        cellRenderer: (params: ICellRendererParams<any>) => {
          const timestamp = !!params.data?.timestamp ? dayjs(params.data?.timestamp) : null
          if (timestamp) {
            return formatDate(timestamp.toString())
          }
        },
        headerName: t('payoutContracts.labels.dateTime'),
        minWidth: 110,
        flex: 1,
        sortable: false,
        filter: false,
        floatingFilter: false,
        cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 0 },
      },
      {
        field: 'action_type',
        cellRenderer: (params: ICellRendererParams<any>) => {
          switch (params.data?.action_type) {
            case 'CRYPTOTRANSFER': {
              return 'Transfer'
            }
          }
          return ''
        },
        headerName: t('payoutContracts.labels.actionType'),
        minWidth: 110,
        flex: 1,
        sortable: false,
        filter: false,
        floatingFilter: false,
        cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 0 },
      },
      {
        field: 'account',
        cellRenderer: (params: ICellRendererParams<any>) => {
          return params.data?.account
        },
        headerName: t('payoutContracts.history.headers.holderAddress'),
        minWidth: 200,
        flex: 1,
        sortable: false,
        filter: false,
        floatingFilter: false,
        cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 0 },
      },
      {
        field: 'amount',
        cellRenderer: (params: ICellRendererParams<any>) => {
          return !!params.data?.amount ? tokenBalanceFormatter(params.data?.amount, payoutToken?.decimals || 0) : ''
        },
        headerName: t('payoutContracts.labels.amount'),
        minWidth: 110,
        flex: 1,
        sortable: false,
        filter: false,
        floatingFilter: false,
        cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 0 },
      },
    ],
    [t],
  )

  const getRows = async (params: IGetRowsParams) => {
    setIsLoading(true)
    const timestamp = paginationState.current.timestamp
    try {
      const queryParams = {
        limit: DEFAULT_PAGE_SIZE,
        offset: 0, // do not matter for infinite scroll,
        showFailed,
      } as any

      if (timestamp) {
        queryParams.timestamp = timestamp
      }

      const result = await getPayoutHistory(payoutContractId, queryParams)
      const newItems = result.data.data
      const newTimestamp = result.data.links.next ? result.data.links.next.split('timestamp=lt:')[1] : undefined

      const hasMore = newItems.length === DEFAULT_PAGE_SIZE && paginationState.current.timestamp !== newTimestamp
      // Save next link for the next request
      if (hasMore) {
        paginationState.current.timestamp = newTimestamp
      }
      const lastRow = hasMore ? undefined : params.startRow + newItems.length
      params.successCallback(newItems, lastRow)
    } catch (error) {
      console.error('Error fetching data', error)
      params.failCallback()
    } finally {
      setIsLoading(false)
    }
  }

  const gridOptions: GridOptions = useMemo(
    () => ({
      defaultColDef: { flex: 1 },
      pagination: true,
      rowModelType: 'infinite', // Or 'serverSide'
      datasource: { getRows: getRows },
      paginationPageSize: 10,
      onGridReady({ api }) {
        api.sizeColumnsToFit()
      },
    }),
    [columnDefs],
  )

  useEffect(() => {
    if (isLoading) {
      gridRef.current?.api?.showLoadingOverlay()
    } else {
      gridRef.current?.api?.hideOverlay()
    }
  }, [isLoading, gridRef])

  return (
    <div>
      <Box
        sx={{ paddingLeft: 0 }}
        maxWidth="xl"
        flexDirection={'row'}
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <Typography align="left" variant="h5">
          {t('payoutContracts.showDetails.tabs.history')}
        </Typography>
        <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
          <Switch
            checked={showFailed}
            onChange={(event) => setShowFailed(event.target.checked)}
            inputProps={{ 'aria-label': 'controlled' }}
          />
          <Typography align="left" variant="body1">
            {t('payoutContracts.labels.showFailed')}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ marginTop: 2 }}>
        <Box display={'flex'} justifyContent={'flex-end'} alignContent={'center'} mb={0}>
          <RefreshIcon
            sx={{ cursor: 'pointer', m: 1, color: 'secondary.main' }}
            onClick={() => {
              gridRef.current?.api?.refreshInfiniteCache()
            }}
          ></RefreshIcon>
        </Box>
        <Box role="table-grid-wrapper" width={'100%'} height={500} className="ag-theme-material">
          <AgGridReact
            ref={gridRef}
            columnDefs={columnDefs}
            rowModelType="infinite"
            cacheBlockSize={DEFAULT_PAGE_SIZE}
            maxBlocksInCache={DEFAULT_PAGE_SIZE}
            pagination={false}
            {...gridOptions}
          ></AgGridReact>
        </Box>
      </Box>
    </div>
  )
}
